/**
 * mcpbTime.js — pure time helpers for the MCPlayback dt-modal (time-nav).
 *
 * The NVR speaks tz-naive wall-clock timestamps 'YYYYMMDDHHMMSS' (state.utility.nvrTimestampShort
 * and event.getEventsByTime params). All arithmetic here treats those as wall-clock and runs the
 * math in UTC epoch ms so adding/subtracting seconds is DST-immune (UTC has no DST). We never tie
 * these to the browser timezone. CloudVS UTC<->local conversion is a Phase 4a concern (the Java
 * MCEventModel.localToUTC path) and is NOT done here — dview/eview cams are already NVR-local.
 *
 * The "Show window" feature: the user picks an anchor time (= window START) + a window duration;
 * Go computes [start, start+window] and sends it to event.getEventsByTime. The 10-min lookback
 * snaps the anchor back if it is within 10 min of the NVR clock (recent clips need disk-write
 * time) — mirrors legacy SearchContainer.js:115-122 (which snaps the query/start point).
 */

// "Show window" chips. `key` matches the mock data-window value; `seconds` is the span Go sends.
export const WINDOW_OPTIONS = [
  { key: '5min', label: '5 min', seconds: 5 * 60 },
  { key: '15min', label: '15 min', seconds: 15 * 60 },
  { key: '1hr', label: '1 hr', seconds: 60 * 60 },
  { key: '6hr', label: '6 hr', seconds: 6 * 60 * 60 },
  { key: '24hr', label: '24 hr', seconds: 24 * 60 * 60 },
];
export const DEFAULT_WINDOW = '5min';

/**
 * windowSeconds — span in seconds for a committed window. A preset KEY ('5min'…) → its seconds; a
 * NUMBER (a Window|Custom range, already = seconds) passes through unchanged; an unknown key → the
 * first preset. A custom range stores its raw seconds (secondsBetween(start, end)) as the window, so
 * every downstream consumer (timeline span, refetch end, event fetch) stays uniform across both modes.
 */
export const windowSeconds = (win) =>
  (typeof win === 'number')
    ? win
    : (WINDOW_OPTIONS.find((o) => o.key === win) || WINDOW_OPTIONS[0]).seconds;

// Compact human duration for a custom (numeric) window. Drops sub-minute noise once we're into
// minutes/hours so the WATCHING chip reads like the presets ('24 min', '1 hr 1 min').
const formatDuration = (totalSec) => {
  const sec = Math.max(0, Math.round(totalSec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return m ? `${h} hr ${m} min` : `${h} hr`;
  if (m) return `${m} min`;
  return `${s} sec`;
};

/**
 * windowLabel — the label on the WATCHING / compact time-control window chip. A preset KEY → its chip
 * label ('5 min'); a NUMBER (custom range) → a compact duration. Unknown key → the first preset label.
 */
export const windowLabel = (win) =>
  (typeof win === 'number')
    ? formatDuration(win)
    : (WINDOW_OPTIONS.find((o) => o.key === win) || WINDOW_OPTIONS[0]).label;

// "Filter by" default set: ALL OFF (David 2026-06-19) — get everything; selecting a chip restricts.
// Shared single source for both the reducer initial state and the dt-modal seed.
// All OFF by default (David 2026-06-19): get everything; selecting a chip restricts to matching clips.
export const DEFAULT_FILTERS = { person: false, vehicle: false, alert: false, archived: false, export: false };

export const LOOKBACK_MIN = 10;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TS_RE = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/;
const pad2 = (n) => ('0' + n).slice(-2);

// 'YYYYMMDDHHMMSS' -> UTC epoch ms (wall-clock arithmetic basis). NaN if malformed.
const tsToMs = (ts) => {
  const m = TS_RE.exec(ts || '');
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : NaN;
};
// UTC epoch ms -> 'YYYYMMDDHHMMSS'.
const msToTs = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}`;
};

/** Build 'YYYYMMDDHHMMSS' from numeric parts (month 1-12). */
export const combineToTimestamp = (year, month, day, h, m, s) =>
  `${year}${pad2(month)}${pad2(day)}${pad2(h)}${pad2(m)}${pad2(s)}`;

/** Build 'YYYYMMDDHHMMSS' from a JS Date's local Y/M/D + explicit h/m/s (the dt-modal inputs). */
export const timestampFromDateAndTime = (date, h, m, s) =>
  combineToTimestamp(date.getFullYear(), date.getMonth() + 1, date.getDate(), +h, +m, +s);

/** Parse 'YYYYMMDDHHMMSS' -> { date: Date(local Y/M/D), h, m, s } (2-char strings) or null. */
export const parseTimestamp = (ts) => {
  const mm = TS_RE.exec(ts || '');
  if (!mm) return null;
  return { date: new Date(+mm[1], +mm[2] - 1, +mm[3]), h: mm[4], m: mm[5], s: mm[6] };
};

/** Add N seconds (may be negative) to a 'YYYYMMDDHHMMSS'. Returns input unchanged if malformed. */
export const addSeconds = (ts, secs) => {
  const ms = tsToMs(ts);
  return isNaN(ms) ? ts : msToTs(ms + secs * 1000);
};

/**
 * Signed whole seconds from `tsFrom` to `tsTo` ('YYYYMMDDHHMMSS' each), DST-immune (UTC math).
 * Positive when tsTo is later. NaN if either is malformed. The basis for clip offsets relative to
 * the playback window start (see util/mcpbClips). Truncates toward zero (NVR timestamps are whole-second).
 */
export const secondsBetween = (tsFrom, tsTo) => {
  const a = tsToMs(tsFrom);
  const b = tsToMs(tsTo);
  return (isNaN(a) || isNaN(b)) ? NaN : Math.trunc((b - a) / 1000);
};

/**
 * 10-min lookback snap: if `anchorTs` is within `lookbackMin` of `nowTs` (or in the future),
 * return nowTs - lookbackMin; otherwise return anchorTs unchanged. Mirrors legacy
 * SearchContainer.js:115-122. `nowTs` should be the NVR clock (nvrTimestampShort), not the browser.
 */
export const applyLookback = (anchorTs, nowTs, lookbackMin = LOOKBACK_MIN) => {
  const a = tsToMs(anchorTs);
  const n = tsToMs(nowTs);
  if (isNaN(a) || isNaN(n)) return anchorTs;
  return n - a < lookbackMin * 60 * 1000 ? msToTs(n - lookbackMin * 60 * 1000) : anchorTs;
};

/** Watch-chip / header time 'HH:MM:SS' from a 'YYYYMMDDHHMMSS' (placeholder if malformed). */
export const chipTime = (ts) => {
  const m = TS_RE.exec(ts || '');
  return m ? `${m[4]}:${m[5]}:${m[6]}` : '--:--:--';
};

/** Wall-clock seconds since local midnight (0..86399) for a 'YYYYMMDDHHMMSS'; 0 if malformed. Basis
 *  for clock-aligned ruler ticks (snap the first tick to a multiple of the tick interval). */
export const secondsOfDay = (ts) => {
  const m = TS_RE.exec(ts || '');
  return m ? (+m[4]) * 3600 + (+m[5]) * 60 + (+m[6]) : 0;
};

/** 'MMM D' date label ('Jul 29') from a 'YYYYMMDDHHMMSS' (empty if malformed) — the playhead pill's date. */
export const shortDate = (ts) => {
  const m = TS_RE.exec(ts || '');
  return m ? `${MONTHS[+m[2] - 1]} ${+m[3]}` : '';
};

// ── Timeline SPAN presets + clock-aligned windowing (port of Java MCTimelineWindow) ─────────────────
// The playback timeline shows a fixed SPAN anchored to a CLOCK boundary (never to the first clip):
// searching 11:38 in a 1-hour span yields the 11:00-12:00 window with the playhead dropped on 11:38
// inside it (Java "one-window rule" AD-1). The four presets mirror MCTimelineWindow.SPAN_PRESETS
// (1h/4h/8h/24h). Because lite timestamps are ABSOLUTE ('YYYYMMDDHHMMSS'), day-rollover on paging falls
// out of addSeconds — no {dayDelta} bookkeeping is threaded back (Java is single-day seconds-from-midnight).
export const MCPB_SPANS = [
  { key: '1h', label: '1h', seconds: 3600 },
  { key: '4h', label: '4h', seconds: 4 * 3600 },
  { key: '8h', label: '8h', seconds: 8 * 3600 },
  { key: '24h', label: '24h', seconds: 24 * 3600 },
];
export const SPAN_SECONDS = MCPB_SPANS.map((s) => s.seconds); // [3600, 14400, 28800, 86400]
export const DEFAULT_SPAN_SEC = 3600;
const SECONDS_PER_DAY = 86400;

/** Snap an arbitrary span to the nearest offered preset (Java MCTimelineWindow.snapSpan); default 1h. */
export const snapSpan = (spanSec) => (SPAN_SECONDS.indexOf(spanSec) >= 0 ? spanSec : DEFAULT_SPAN_SEC);

/** span seconds -> its preset label ('4h'); a non-preset numeric span falls back to windowLabel(). */
export const spanLabel = (spanSec) => {
  const p = MCPB_SPANS.find((s) => s.seconds === spanSec);
  return p ? p.label : windowLabel(spanSec);
};

/**
 * Floor a timestamp to the SPAN boundary at or below it, within the day — the port of Java
 * MCTimelineWindow.floorToSpan ((searchSec / spanSec) * spanSec on seconds-from-midnight). The one-window
 * rule (AD-1): 11:38 @ 1h -> window start 11:00. A malformed ts is returned unchanged; spanSec<=0 guards to 1h.
 */
export const floorToSpan = (ts, spanSec) => {
  const span = spanSec > 0 ? spanSec : DEFAULT_SPAN_SEC;
  const sod = secondsOfDay(ts);
  const floored = Math.floor(sod / span) * span;
  return addSeconds(ts, floored - sod); // shift back to the boundary on the same day (addSeconds no-ops malformed)
};

/**
 * Page a clock-aligned window by one SPAN (unit='span') or one DAY (unit='day') in `dir` (+1 fwd / -1 back).
 * Returns the new window START timestamp, re-floored to the span first so paging from an unaligned jump
 * position snaps back onto the grid (Java MCTimelineWindow.pageBySpan/pageByDay). Day-rollover is handled by
 * addSeconds (absolute epoch), so unlike Java there is no dayDelta to thread back to the caller.
 */
export const pageWindow = (windowStartTs, spanSec, dir, unit = 'span') => {
  const span = spanSec > 0 ? spanSec : DEFAULT_SPAN_SEC;
  const aligned = floorToSpan(windowStartTs, span);
  const step = unit === 'day' ? SECONDS_PER_DAY : span;
  return addSeconds(aligned, (dir < 0 ? -1 : 1) * step);
};

/**
 * Watch-chip date label relative to the NVR "now": 'Today · Jun 15' / 'Yesterday · Jun 14' /
 * 'Jun 12'. Day difference is computed on UTC-midnight of each wall-clock day (exact calendar days).
 */
export const chipDate = (ts, nowTs) => {
  const m = TS_RE.exec(ts || '');
  if (!m) return '';
  const label = `${MONTHS[+m[2] - 1]} ${+m[3]}`;
  const n = TS_RE.exec(nowTs || '');
  if (n) {
    const day = (x) => Date.UTC(+x[1], +x[2] - 1, +x[3]);
    const diffDays = Math.round((day(n) - day(m)) / 86400000);
    if (diffDays === 0) return `Today · ${label}`;
    if (diffDays === 1) return `Yesterday · ${label}`;
  }
  return label;
};
